import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Drop,
  Plus,
  Trash,
  PencilSimple,
  Camera,
  CircleNotch,
  Eraser,
  Link as LinkIcon,
} from "@phosphor-icons/react";
import { PhotoGallery } from "@/components/PhotoGallery";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AppHeader } from "@/components/AppHeader";
import { FilterBar } from "@/components/FilterBar";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function Sinks() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [view, setView] = useState("Gallery");
  const [stockFilter, setStockFilter] = useState("All");
  const [linksOpen, setLinksOpen] = useState(false);
  const [linkDraft, setLinkDraft] = useState({});
  const [savingLinks, setSavingLinks] = useState(false);
  const photoInputs = useRef({});
  const photoSrc = (p) =>
    `${process.env.REACT_APP_BACKEND_URL}/api/photos/${p.storage_path}?auth=${localStorage.getItem(
      "sw_token",
    )}`;

  const uploadPhotos = async (sink, e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (picked.length === 0) return;
    const fd = new FormData();
    picked.forEach((f) => fd.append("files", f));
    try {
      await api.post(`/sinks/${sink.id}/photos`, fd);
      toast.success(
        picked.length === 1 ? "Photo added" : `${picked.length} photos added`,
      );
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Photo upload failed",
      );
    }
  };

  const removePhoto = async (sink, photoId) => {
    await api.delete(`/sinks/${sink.id}/photos/${photoId}`);
    toast.success("Photo removed");
    load();
  };

  const [cutting, setCutting] = useState("");
  const [cleaning, setCleaning] = useState(false);
  const cleanAll = async () => {
    if (!window.confirm("Remove the background from every sink photo that has a plain white backdrop? Originals are kept for undo.")) return;
    setCleaning(true);
    try {
      const { data } = await api.post("/sinks/photos/cutout-all");
      toast.success(`${data.done} photo${data.done === 1 ? "" : "s"} cleaned${data.skipped ? ` · ${data.skipped} skipped (busy background)` : ""}${data.failed ? ` · ${data.failed} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk cleanup failed");
    } finally {
      setCleaning(false);
    }
  };
  const cutoutPhoto = async (sink, photo) => {
    setCutting(photo.id);
    try {
      if (photo.cutout) {
        await api.post(`/sinks/${sink.id}/photos/${photo.id}/cutout/undo`);
        toast.success("Original photo restored");
      } else {
        await api.post(`/sinks/${sink.id}/photos/${photo.id}/cutout`);
        toast.success("Background removed");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not edit the photo");
    } finally {
      setCutting("");
    }
  };

  const load = useCallback(async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (stockFilter === "Supplier stock") params.supplier_stock = true;
    else if (stockFilter === "Tracked") params.supplier_stock = false;
    else if (stockFilter === "Low") params.low = true;
    else if (stockFilter === "Completed") params.completed = true;
    const list = await api.get("/sinks", { params });
    setItems(list.data);
  }, [q, stockFilter]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const remove = async (s) => {
    await api.delete(`/sinks/${s.id}`);
    toast.success("Sink removed");
    load();
  };

  const openLinks = () => {
    const draft = {};
    items.forEach((s) => {
      draft[s.id] = s.template_url || "";
    });
    setLinkDraft(draft);
    setLinksOpen(true);
  };

  const saveLinks = async () => {
    const updates = items
      .filter((s) => (linkDraft[s.id] || "") !== (s.template_url || ""))
      .map((s) => ({ id: s.id, template_url: (linkDraft[s.id] || "").trim() }));
    if (updates.length === 0) {
      setLinksOpen(false);
      return;
    }
    setSavingLinks(true);
    try {
      const res = await api.patch("/sinks/template-urls", { updates });
      toast.success(
        `${res.data?.updated ?? updates.length} spec link${
          (res.data?.updated ?? updates.length) === 1 ? "" : "s"
        } saved`,
      );
      setLinksOpen(false);
      load();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not save spec links",
      );
    } finally {
      setSavingLinks(false);
    }
  };

  const onHand = items.reduce(
    (n, s) => n + (s.stocked === false ? 0 : s.quantity || 0),
    0,
  );
  const lowCount = items.filter(
    (s) => s.stocked !== false && s.quantity <= s.min_level,
  ).length;
  const assigned = items.reduce(
    (n, s) => n + (s.allocations || []).reduce((m, a) => m + (a.qty || 1), 0),
    0,
  );

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Sinks" />
      <main className="relative z-10 pb-28 pt-6">
        <div
          className="rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="sinks-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                Inventory
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                Sink Stock
              </h1>
              <p
                className="mt-1 text-sm text-zinc-500"
                data-testid="sinks-summary"
              >
                {items.length} model{items.length === 1 ? "" : "s"} · {onHand}{" "}
                on hand · {assigned} reserved to work
              </p>
            </div>
          </div>

          <div
            className="mt-5 flex flex-wrap gap-2 border-t border-zinc-800 pt-4"
            data-testid="sinks-actions"
          >
            <Button
              data-testid="add-sink-button"
              onClick={() => navigate("/inventory/sinks/new")}
              className="h-12 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
            >
              <Plus size={15} weight="bold" className="mr-2" /> Add Sink
            </Button>
            <Button
              data-testid="sinks-import-button"
              variant="outline"
              onClick={() => navigate("/settings/import-sinks")}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              Import CSV
            </Button>
            <Button
              data-testid="sinks-clean-photos-button"
              variant="outline"
              disabled={cleaning}
              onClick={cleanAll}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              {cleaning ? <CircleNotch size={15} weight="bold" className="mr-2 animate-spin" /> : <Eraser size={15} weight="bold" className="mr-2" />}
              {cleaning ? "Cleaning…" : "Clean all photos"}
            </Button>
            <Button
              data-testid="sinks-spec-links-button"
              variant="outline"
              onClick={openLinks}
              disabled={items.length === 0}
              className="h-12 border-zinc-700 bg-transparent px-4 text-[11px] uppercase tracking-widest text-zinc-300 transition-colors duration-150 hover:bg-zinc-800"
            >
              <LinkIcon size={14} weight="bold" className="mr-2" /> Spec Links
            </Button>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            ["Models", items.length, "text-white"],
            ["On Hand", onHand, "text-emerald-400"],
            ["Reserved", assigned, "text-indigo-300"],
            [
              "Low Stock",
              lowCount,
              lowCount ? "text-red-400" : "text-zinc-400",
            ],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`sink-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6">
          <div data-testid="sinks-list">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start">
              <FilterBar
                className="min-w-0 flex-1"
                testidPrefix="sinks"
                search={{
                  value: q,
                  onChange: setQ,
                  placeholder: "Search brand, model, supplier, shelf…",
                  testid: "sink-search",
                }}
                sections={[
                  {
                    key: "stock",
                    label: "Stock",
                    value: stockFilter,
                    allValue: "All",
                    onChange: setStockFilter,
                    options: ["All", "Tracked", "Supplier stock", "Low", "Completed"].map(
                      (f) => [
                        f,
                        f,
                        `sink-filter-${f.toLowerCase().replace(/ /g, "-")}`,
                      ],
                    ),
                  },
                ]}
              />
              <div
                className="flex gap-2 overflow-x-auto pb-1"
                data-testid="sink-stock-filter"
              >
                <div
                  className="flex shrink-0 overflow-hidden rounded-md border border-zinc-800"
                  data-testid="sink-view-toggle"
                >
                  {["Cards", "Gallery", "Compact"].map((v) => (
                    <button
                      key={v}
                      data-testid={`sink-view-${v.toLowerCase()}`}
                      onClick={() => setView(v)}
                      className={`h-12 px-3 text-[11px] font-semibold uppercase tracking-widest transition-colors duration-150 ${
                        view === v
                          ? "bg-indigo-500 text-white"
                          : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                      }`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            {items.length === 0 && (
              <div className="rounded-lg border border-dashed border-zinc-800 p-10 text-center">
                <Drop
                  size={26}
                  weight="bold"
                  className="mx-auto text-zinc-700"
                />
                <p className="mt-3 text-sm text-zinc-400">
                  No sinks in stock yet.
                </p>
              </div>
            )}

            {view === "Gallery" && (
              <div
                className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4"
                data-testid="sinks-gallery"
              >
                {items.map((s) => {
                  const photos = (s.photos || []).filter((p) => !p.is_deleted);
                  const low = s.quantity <= s.min_level;
                  const stockCls =
                    s.stocked === false
                      ? "bg-amber-500/15 text-amber-200 ring-amber-400/40"
                      : low
                        ? "bg-red-500/15 text-red-200 ring-red-400/40"
                        : "bg-emerald-500/15 text-emerald-200 ring-emerald-400/40";
                  return (
                    <div
                      key={s.id}
                      data-testid={`sink-tile-${s.id}`}
                      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/[0.07] bg-zinc-900/80 shadow-[0_18px_40px_-24px_rgba(0,0,0,0.9)] transition-all duration-200 hover:-translate-y-0.5 hover:border-amber-400/30 hover:shadow-[0_24px_50px_-24px_rgba(195,158,109,0.35)]"
                    >
                      <div className={`relative h-60 w-full shrink-0 ${photos[0]?.cutout ? "bg-[radial-gradient(ellipse_at_50%_35%,#2a2f3a_0%,#15181f_70%)]" : "bg-[radial-gradient(ellipse_at_center,#f5f5f4_0%,#d6d3d1_100%)]"}`}>
                        {photos[0]?.cutout && (
                          <span className="pointer-events-none absolute inset-x-10 bottom-6 h-6 rounded-[100%] bg-black/50 blur-xl" />
                        )}
                        {photos.length > 0 ? (
                          <Link to={`/inventory/sinks/${s.id}`} data-testid={`sink-photo-open-${s.id}`} className="block h-full w-full p-4">
                            <img
                              src={photoSrc(photos[0])}
                              alt={s.model}
                              className={`h-full w-full object-contain transition-transform duration-300 group-hover:scale-[1.05] ${photos[0]?.cutout ? "drop-shadow-[0_18px_22px_rgba(0,0,0,0.6)]" : "drop-shadow-[0_10px_18px_rgba(0,0,0,0.35)]"}`}
                            />
                          </Link>
                        ) : (
                          <button
                            onClick={() => photoInputs.current[s.id]?.click()}
                            data-testid={`sink-add-photo-${s.id}`}
                            className="flex h-full w-full flex-col items-center justify-center gap-2 text-[11px] font-bold uppercase tracking-widest text-stone-500 hover:text-stone-700"
                          >
                            <Camera size={22} weight="bold" /> Add photo
                          </button>
                        )}
                        <span
                          data-testid={`sink-stock-${s.id}`}
                          className={`absolute left-3 top-3 rounded-full px-2.5 py-1 font-mono-data text-[11px] font-bold ring-1 backdrop-blur ${stockCls}`}
                        >
                          {s.stocked === false ? "Supplier stock" : `${s.quantity} on hand`}
                        </span>
                        {s.consumed_project_code && (
                          <Link
                            to={`/projects/${s.consumed_project_id}`}
                            data-testid={`sink-consumed-link-${s.id}`}
                            className="absolute right-3 top-3 rounded-full border border-amber-500/30 bg-amber-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-widest text-amber-300 backdrop-blur transition-colors hover:bg-amber-500/20"
                          >
                            Completed · {s.consumed_project_code}
                          </Link>
                        )}
                        {photos.length > 1 && (
                          <span className="absolute bottom-3 right-3 rounded-full bg-zinc-950/70 px-2 py-0.5 text-[10px] font-bold text-zinc-200 backdrop-blur">
                            +{photos.length - 1} photo{photos.length > 2 ? "s" : ""}
                          </span>
                        )}
                        <span className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
                          {photos.length > 0 && (
                            <button
                              data-testid={`sink-photo-cutout-${s.id}`}
                              disabled={cutting === photos[0].id}
                              onClick={() => cutoutPhoto(s, photos[0])}
                              title={photos[0].cutout ? "Restore original photo" : "Remove background"}
                              className={`rounded-full p-2 disabled:opacity-50 ${photos[0].cutout ? "bg-amber-500 text-zinc-950 hover:bg-amber-400" : "bg-zinc-950/80 text-zinc-200 hover:bg-zinc-950"}`}
                            >
                              {cutting === photos[0].id ? <CircleNotch size={14} weight="bold" className="animate-spin" /> : <Eraser size={14} weight="bold" />}
                            </button>
                          )}
                          <button
                            data-testid={`sink-photo-upload-${s.id}`}
                            onClick={() => photoInputs.current[s.id]?.click()}
                            title="Add photos"
                            className="rounded-full bg-zinc-950/80 p-2 text-zinc-200 hover:bg-zinc-950"
                          >
                            <Camera size={14} weight="bold" />
                          </button>
                          <button
                            data-testid={`sink-edit-${s.id}`}
                            onClick={() => navigate(`/inventory/sinks/${s.id}/edit`)}
                            title="Edit"
                            className="rounded-full bg-zinc-950/80 p-2 text-zinc-200 hover:bg-zinc-950"
                          >
                            <PencilSimple size={14} weight="bold" />
                          </button>
                          <button
                            data-testid={`sink-remove-${s.id}`}
                            onClick={() => remove(s)}
                            title="Remove"
                            className="rounded-full bg-zinc-950/80 p-2 text-zinc-300 hover:bg-red-600 hover:text-white"
                          >
                            <Trash size={14} weight="bold" />
                          </button>
                        </span>
                      </div>
                      <input
                        ref={(el) => (photoInputs.current[s.id] = el)}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => uploadPhotos(s, e)}
                      />
                      <Link
                        to={`/inventory/sinks/${s.id}`}
                        data-testid={`sink-open-${s.id}`}
                        className="flex flex-1 flex-col gap-1 p-4 hover:bg-white/[0.03]"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-mono-data text-[11px] text-yellow-500">{s.code}</span>
                          {s.sink_type && (
                            <span className="rounded-full border border-zinc-700 px-2 py-0.5 text-[10px] uppercase tracking-widest text-zinc-400">
                              {s.sink_type}
                            </span>
                          )}
                        </div>
                        <h3 className="line-clamp-2 text-sm font-bold leading-snug text-zinc-100">
                          {[s.brand, s.model].filter(Boolean).join(" ")}
                        </h3>
                        {s.description && (
                          <p className="line-clamp-1 text-xs text-zinc-500">{s.description}</p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-1" data-testid={`sink-chips-${s.id}`}>
                          {[
                            s.bowls && `${s.bowls} bowl`,
                            s.material,
                            s.cutout_size && `Cutout ${s.cutout_size}`,
                            s.size && `Depth ${(s.size.match(/x\s*[\d.½¼¾"]+\s*x\s*([\d.½¼¾]+)/i) || [])[1] ? (s.size.match(/x\s*[\d.½¼¾"]+\s*x\s*([\d.½¼¾]+)/i) || [])[1] + '"' : ""}`.replace(/^Depth $/, ""),
                          ]
                            .filter(Boolean)
                            .map((c) => (
                              <span key={c} className="rounded-md border border-zinc-700/80 bg-zinc-800/60 px-1.5 py-0.5 text-[10px] font-semibold text-zinc-300">
                                {c}
                              </span>
                            ))}
                        </div>
                        <div className="mt-auto pt-1 text-xs text-zinc-400">
                          {[s.size, s.location && `shelf ${s.location}`].filter(Boolean).join(" · ")}
                        </div>
                      </Link>
                    </div>
                  );
                })}
              </div>
            )}

            {view === "Compact" && items.length > 0 && (
              <div
                className="overflow-hidden rounded-lg border border-zinc-800 bg-zinc-900"
                data-testid="sinks-compact"
              >
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500">
                      <th className="p-3">Code</th>
                      <th className="p-3">Sink</th>
                      <th className="p-3">Type</th>
                      <th className="p-3">Shelf</th>
                      <th className="p-3 text-right">On hand</th>
                      <th className="p-3 text-right">Reserved</th>
                      <th className="p-3" />
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((s) => {
                      const low = s.quantity <= s.min_level;
                      const reserved = (s.allocations || []).reduce(
                        (m, a) => m + (a.qty || 1),
                        0,
                      );
                      return (
                        <tr
                          key={s.id}
                          data-testid={`sink-row-${s.id}`}
                          className="border-b border-zinc-800 last:border-0"
                        >
                          <td className="font-mono-data p-3 text-xs text-yellow-500">
                            <Link
                              to={`/inventory/sinks/${s.id}`}
                              data-testid={`sink-open-${s.id}`}
                            >
                              {s.code}
                            </Link>
                          </td>
                          <td className="p-3 font-semibold">
                            {[s.brand, s.model].filter(Boolean).join(" ")}
                            {s.description && (
                              <span className="block text-xs font-normal text-zinc-500">
                                {s.description}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-zinc-400">{s.sink_type}</td>
                          <td className="p-3 text-zinc-400">
                            {s.location || "—"}
                          </td>
                          <td
                            className={`font-mono-data p-3 text-right font-bold ${
                              s.stocked === false
                                ? "text-amber-400"
                                : low
                                  ? "text-red-400"
                                  : "text-emerald-400"
                            }`}
                          >
                            {s.stocked === false
                              ? "Supplier stock"
                              : s.quantity}
                          </td>
                          <td className="font-mono-data p-3 text-right text-indigo-300">
                            {reserved || "—"}
                          </td>
                          <td className="p-3">
                            <span className="flex justify-end gap-3">
                              <button
                                data-testid={`sink-edit-${s.id}`}
                                onClick={() =>
                                  navigate(`/inventory/sinks/${s.id}/edit`)
                                }
                                className="text-zinc-500 hover:text-white"
                              >
                                <PencilSimple size={14} weight="bold" />
                              </button>
                              <button
                                data-testid={`sink-remove-${s.id}`}
                                onClick={() => remove(s)}
                                className="text-zinc-600 hover:text-red-400"
                              >
                                <Trash size={14} weight="bold" />
                              </button>
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {view === "Cards" &&
              items.map((s) => {
                const low = s.quantity <= s.min_level;
                const reserved = (s.allocations || []).reduce(
                  (m, a) => m + (a.qty || 1),
                  0,
                );
                return (
                  <div
                    key={s.id}
                    data-testid={`sink-card-${s.id}`}
                    className="mt-2 rounded-lg border border-zinc-800 bg-zinc-900 p-4"
                  >
                    <div className="flex flex-wrap items-center gap-4">
                      <Link
                        to={`/inventory/sinks/${s.id}`}
                        data-testid={`sink-open-${s.id}`}
                        className="min-w-0 flex-1 hover:opacity-80"
                      >
                        <div className="font-mono-data text-xs text-yellow-500">
                          {s.code}
                        </div>
                        <h3 className="truncate text-base font-bold">
                          {[s.brand, s.model].filter(Boolean).join(" ")}
                        </h3>
                        {s.description && (
                          <p className="truncate text-xs text-zinc-400">
                            {s.description}
                          </p>
                        )}
                        <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-xs text-zinc-400">
                          <span>
                            {s.sink_type} · {s.bowls} bowl · {s.material}
                          </span>
                          {s.size && (
                            <span className="font-mono-data">{s.size}</span>
                          )}
                          {s.supplier && <span>{s.supplier}</span>}
                          {s.location && (
                            <span>
                              <span className="text-zinc-600">Shelf </span>
                              {s.location}
                            </span>
                          )}
                        </div>
                      </Link>
                      <div className="text-right">
                        {s.stocked === false ? (
                          <div className="rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-amber-400">
                            Supplier stock
                          </div>
                        ) : (
                          <>
                            <div
                              className={`font-mono-data text-lg font-bold ${
                                low ? "text-red-400" : "text-emerald-400"
                              }`}
                            >
                              {s.quantity} on hand
                            </div>
                            <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                              min {s.min_level}
                              {reserved ? ` · ${reserved} reserved` : ""}
                            </div>
                          </>
                        )}
                      </div>
                      <button
                        data-testid={`sink-edit-${s.id}`}
                        onClick={() =>
                          navigate(`/inventory/sinks/${s.id}/edit`)
                        }
                        className="text-zinc-500 hover:text-white"
                      >
                        <PencilSimple size={15} weight="bold" />
                      </button>
                      <button
                        data-testid={`sink-remove-${s.id}`}
                        onClick={() => remove(s)}
                        className="text-zinc-600 hover:text-red-400"
                      >
                        <Trash size={15} weight="bold" />
                      </button>
                    </div>
                    <div className="mt-3 border-t border-zinc-800 pt-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                          Photos (
                          {(s.photos || []).filter((p) => !p.is_deleted).length}
                          )
                        </div>
                        <button
                          data-testid={`sink-add-photos-${s.id}`}
                          onClick={() => photoInputs.current[s.id]?.click()}
                          className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-yellow-400 hover:underline"
                        >
                          <Camera size={13} weight="bold" /> Add photos
                        </button>
                        <input
                          ref={(el) => (photoInputs.current[s.id] = el)}
                          data-testid={`sink-photo-input-${s.id}`}
                          type="file"
                          accept="image/*"
                          multiple
                          className="hidden"
                          onChange={(e) => uploadPhotos(s, e)}
                        />
                      </div>
                      <PhotoGallery
                        photos={(s.photos || []).filter((p) => !p.is_deleted)}
                        onRemove={(photoId) => removePhoto(s, photoId)}
                        testPrefix={`sink-photo-${s.id}`}
                      />
                    </div>
                    {(s.allocations || []).length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2 border-t border-zinc-800 pt-3">
                        {s.allocations.map((a) => (
                          <span
                            key={a.id}
                            data-testid={`sink-alloc-${a.id}`}
                            className="inline-flex items-center gap-1 rounded-full border border-indigo-400/25 bg-indigo-500/10 px-2.5 py-1 text-[10px] uppercase tracking-widest text-indigo-300"
                          >
                            <LinkIcon size={11} weight="bold" />{" "}
                            {a.label || a.entity} × {a.qty}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </main>

      {linksOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-0 sm:items-center sm:p-4"
          data-testid="sink-links-modal"
          onClick={() => !savingLinks && setLinksOpen(false)}
        >
          <div
            className="flex max-h-[90vh] w-full flex-col rounded-t-2xl border border-zinc-800 bg-zinc-900 sm:max-w-2xl sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-b border-zinc-800 px-5 py-4">
              <h2 className="text-base font-bold">Manufacturer spec-sheet links</h2>
              <p className="mt-1 text-xs text-zinc-500">
                Paste each sink's cut-out template / spec-sheet URL. These print
                next to the cut-out on quote PDFs so fabricators can open the spec.
              </p>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">
              {items.length === 0 ? (
                <p className="text-sm text-zinc-500">No sinks to link.</p>
              ) : (
                <div className="space-y-3">
                  {items.map((s) => (
                    <div
                      key={s.id}
                      className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3"
                      data-testid={`sink-link-row-${s.id}`}
                    >
                      <div className="min-w-0 sm:w-48 sm:shrink-0">
                        <div className="font-mono-data text-[11px] text-yellow-500">
                          {s.code}
                        </div>
                        <div className="truncate text-sm font-semibold">
                          {[s.brand, s.model].filter(Boolean).join(" ") || "—"}
                        </div>
                      </div>
                      <Input
                        data-testid={`sink-link-input-${s.id}`}
                        type="url"
                        inputMode="url"
                        placeholder="https://manufacturer.com/spec.pdf"
                        value={linkDraft[s.id] ?? ""}
                        onChange={(e) =>
                          setLinkDraft((d) => ({ ...d, [s.id]: e.target.value }))
                        }
                        className="h-11 flex-1 bg-zinc-950 text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-zinc-800 px-5 py-4">
              <Button
                data-testid="sink-links-cancel"
                variant="outline"
                onClick={() => setLinksOpen(false)}
                disabled={savingLinks}
                className="h-11 border-zinc-700 bg-transparent px-5 text-xs uppercase tracking-widest text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                data-testid="sink-links-save"
                onClick={saveLinks}
                disabled={savingLinks || items.length === 0}
                className="h-11 bg-indigo-500 px-6 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                {savingLinks ? "Saving…" : "Save all"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
